import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Button,
  TextField,
  MenuItem,
  CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import moment from "moment";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 24,
    overflow: "hidden",
    maxWidth: 540,
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      margin: 10,
      borderRadius: 22
    }
  },
  header: {
    padding: "18px 20px",
    background: "linear-gradient(135deg,#0F1B35,#2563EB)",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  title: {
    fontWeight: 950,
    fontSize: 18
  },
  subtitle: {
    fontSize: 12,
    opacity: .78,
    marginTop: 3
  },
  content: {
    padding: 20,
    background: "#F8FAFC"
  },
  card: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 18,
    padding: 14,
    marginBottom: 12,
    boxShadow: "0 10px 24px rgba(15,23,42,.04)"
  },
  label: {
    fontSize: 12,
    fontWeight: 900,
    color: "#334155",
    marginBottom: 8
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  actions: {
    padding: 16,
    background: "#FFFFFF",
    borderTop: "1px solid #E2E8F0",
    display: "flex",
    gap: 10,
    justifyContent: "flex-end"
  },
  cancelBtn: {
    borderRadius: 999,
    fontWeight: 800,
    textTransform: "none"
  },
  saveBtn: {
    borderRadius: 999,
    fontWeight: 900,
    textTransform: "none",
    padding: "9px 18px",
    background: "linear-gradient(135deg,#2563EB,#1D4ED8)",
    color: "#FFFFFF",
    boxShadow: "0 12px 24px rgba(37,99,235,.22)",
    "&:hover": {
      background: "linear-gradient(135deg,#1D4ED8,#1E40AF)"
    }
  }
}));

const KoraxFollowUpModal = ({
  open,
  onClose,
  ticketId,
  contactId,
  whatsappId,
  scheduleId,
  reload,
  cleanContact
}) => {
  const classes = useStyles();

  const [message, setMessage] = useState("");
  const [date, setDate] = useState(moment().add(1, "day").format("YYYY-MM-DD"));
  const [time, setTime] = useState("09:00");
  const [selectedContactId, setSelectedContactId] = useState(contactId || "");
  const [contacts, setContacts] = useState([]);
  const [audioKey, setAudioKey] = useState("");
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWhatsappId, setSelectedWhatsappId] =
    useState("");

  const { user } = useContext(AuthContext);
  const { whatsApps } = useWhatsApps();

  const availableWhatsApps = useMemo(() => {
    const list = Array.isArray(whatsApps)
      ? whatsApps
      : [];

    const hasRestriction =
      user?.profile !== "admin" &&
      Array.isArray(user?.whatsappIds);

    if (!hasRestriction) {
      return list;
    }

    const allowedIds =
      user.whatsappIds
        .map(Number)
        .filter(
          id =>
            Number.isInteger(id) &&
            id > 0
        );

    return list.filter(item =>
      allowedIds.includes(Number(item.id))
    );
  }, [
    whatsApps,
    user?.profile,
    user?.whatsappIds
  ]);

  useEffect(() => {
    if (!open || scheduleId) return;

    const currentWhatsapp = availableWhatsApps.find(
      item =>
        Number(item.id) ===
        Number(whatsappId)
    );

    if (currentWhatsapp) {
      setSelectedWhatsappId(
        currentWhatsapp.id
      );
      return;
    }

    if (availableWhatsApps.length === 1) {
      setSelectedWhatsappId(
        availableWhatsApps[0].id
      );
      return;
    }

    setSelectedWhatsappId("");
  }, [
    open,
    scheduleId,
    whatsappId,
    availableWhatsApps
  ]);

  useEffect(() => {
    if (!open) return;

    async function loadData() {
      try {
        const { data } = await api.get("/contacts/list");
        const list = Array.isArray(data) ? data : data?.contacts || [];
        setContacts(list);
      } catch (err) {
        setContacts([]);
      }

      try {
        const { data } = await api.get("/cavia-medias");
        const list = Array.isArray(data) ? data : data?.medias || data?.rows || [];
        setAudios(list.filter(m => String(m.type || m.mediaType || "").toLowerCase().includes("audio")));
      } catch (err) {
        setAudios([]);
      }

      if (scheduleId) {
        try {
          const { data } = await api.get(`/schedules/${scheduleId}`);
          setMessage(data.body || "");
          setSelectedContactId(data.contactId || data.contact?.id || "");
          setDate(moment(data.sendAt).format("YYYY-MM-DD"));
          setTime(moment(data.sendAt).format("HH:mm"));
          setSelectedWhatsappId(
            data.whatsappId ||
            data.whatsapp?.id ||
            ""
          );
        } catch (err) {
          toastError(err);
        }
      }
    }

    loadData();
  }, [open, scheduleId]);

  const reset = () => {
    setMessage("");
    setAudioKey("");
    setSelectedContactId(contactId || "");
    setDate(moment().add(1, "day").format("YYYY-MM-DD"));
    setTime("09:00");
    setSelectedWhatsappId("");
  };

  const handleClose = () => {
    reset();
    if (typeof cleanContact === "function") cleanContact();
    onClose();
  };

  const handleSave = async () => {
    if (!selectedContactId && !ticketId) {
      toast.error("Selecione um contato.");
      return;
    }

    if (!message.trim() && !audioKey) {
      toast.error("Digite uma mensagem ou selecione um áudio.");
      return;
    }

    if (!date || !time) {
      toast.error("Escolha data e hora.");
      return;
    }

    const effectiveWhatsappId =
      selectedWhatsappId || whatsappId;

    if (!effectiveWhatsappId) {
      toast.error(
        "Selecione a conexão WhatsApp para enviar este follow-up."
      );
      return;
    }

    const whatsappAllowed =
      availableWhatsApps.some(
        item =>
          Number(item.id) ===
          Number(effectiveWhatsappId)
      );

    if (!whatsappAllowed) {
      toast.error(
        "Você não possui acesso a essa conexão."
      );
      return;
    }

    setLoading(true);

    try {
      const sendAt = moment(`${date} ${time}`, "YYYY-MM-DD HH:mm").toISOString();

      const payload = {
        body: message,
        contactId: selectedContactId || contactId || null,
        ticketId: ticketId || null,
        whatsappId: Number(
          selectedWhatsappId || whatsappId
        ),
        sendAt,
        openTicket: "enabled",
        statusTicket: "open",
        mediaKey: audioKey || null
      };

      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, payload);
      } else {
        await api.post("/schedules", payload);
      }

      toast.success(scheduleId ? "Follow-up atualizado." : "Follow-up agendado.");
      if (typeof reload === "function") reload();
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.paper }} fullWidth maxWidth="sm">
      <div className={classes.header}>
        <div>
          <div className={classes.title}>⚡ {scheduleId ? "Editar Follow-up" : "Novo Follow-up"}</div>
          <div className={classes.subtitle}>Mensagem, áudio, data e hora. Só o necessário.</div>
        </div>

        <IconButton onClick={handleClose} style={{ color: "#FFFFFF" }}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.content}>
        {!ticketId && (
          <div className={classes.card}>
            <div className={classes.label}>Lead / contato</div>
            <TextField
              select
              value={selectedContactId}
              onChange={e => setSelectedContactId(e.target.value)}
              variant="outlined"
              fullWidth
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Selecione um contato</MenuItem>
              {contacts.map(contact => (
                <MenuItem key={contact.id} value={contact.id}>
                  {contact.name} {contact.number ? `• ${contact.number}` : ""}
                </MenuItem>
              ))}
            </TextField>
          </div>
        )}

        <div className={classes.card}>
          <div className={classes.label}>Mensagem</div>
          <TextField
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Ex: Oi {{nome}}, passando para saber se posso te ajudar..."
            variant="outlined"
            fullWidth
            multiline
            minRows={4}
          />
        </div>

        <div className={classes.card}>
          <div className={classes.label}>Áudio da biblioteca Korax (opcional)</div>
          <TextField
            select
            value={audioKey}
            onChange={e => setAudioKey(e.target.value)}
            variant="outlined"
            fullWidth
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">Nenhum áudio</MenuItem>
            {audios.map(audio => (
              <MenuItem key={audio.id || audio.mediaKey || audio.key} value={audio.mediaKey || audio.key || audio.id}>
                {audio.name || audio.mediaName || audio.title || `Áudio ${audio.id}`}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div className={classes.card}>
          <div className={classes.label}>
            Enviar pelo WhatsApp
          </div>

          <TextField
            select
            value={selectedWhatsappId}
            onChange={e =>
              setSelectedWhatsappId(e.target.value)
            }
            variant="outlined"
            fullWidth
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">
              Selecione uma conexão
            </MenuItem>

            {availableWhatsApps.map(item => (
              <MenuItem
                key={item.id}
                value={item.id}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    backgroundColor: "#22C55E",
                    display: "inline-block",
                    marginRight: 9,
                    flexShrink: 0
                  }}
                />
                {item.name}
              </MenuItem>
            ))}
          </TextField>

          {availableWhatsApps.length === 0 && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: "#DC2626",
                fontWeight: 700
              }}
            >
              Nenhuma conexão WhatsApp está liberada
              para este usuário.
            </div>
          )}
        </div>

        <div className={classes.card}>
          <div className={classes.label}>Quando enviar?</div>
          <div className={classes.row}>
            <TextField label="Data" type="date" variant="outlined" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Hora" type="time" variant="outlined" value={time} onChange={e => setTime(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </div>
        </div>
      </DialogContent>

      <div className={classes.actions}>
        <Button onClick={handleClose} className={classes.cancelBtn} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSave} className={classes.saveBtn} disabled={loading}>
          {loading ? <CircularProgress size={20} style={{ color: "#FFFFFF" }} /> : "Salvar Follow-up"}
        </Button>
      </div>
    </Dialog>
  );
};

export default KoraxFollowUpModal;
